/**
 * Trial-Site Install Orchestrator (Stage A — optional).
 *
 * One-shot, idempotent install of an awarded trial at a site:
 *   1. Expand trial_protocols.visit_schedule -> study_visits rows
 *   2. Provision the chosen eCRF pack into trial_protocol_forms
 *   3. Bundle every visit to its applicable forms in visit_form_bundles
 *   4. Verify randomisation_schedules exists (advisory, not blocking)
 *   5. Write a 21 CFR Part 11 audit row
 *
 * Skips Stage B (per-patient enroll) and Stage C (per-visit billing) —
 * those are layered in once the site has used the wizard once.
 *
 * Hard rule: this hook is OPTIONAL. Sites can keep using manual
 * tab-based workflows; the wizard only pre-fills.
 */

import { useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { provisionECRFPack, type ProvisionedForm } from '@/components/ecrf/templates/provisionECRFPack';

export interface VisitDef {
  code: string;
  label?: string;
  day?: number;
  window_before?: number;
  window_after?: number;
  visit_type?: string;
}

export interface InstallTrialInput {
  trialId: string;             // cro_trials.id  (also referenced from trial_protocols.trial_id)
  trialSiteId: string;
  packCode?: string;           // optional — if omitted, eCRF provisioning is skipped
}

export interface InstallTrialResult {
  visitsCreated: number;
  formsProvisioned: number;
  bundlesCreated: number;
  randomisationReady: boolean;
  protocolId: string | null;
  pack: { code: string; name: string; version: string } | null;
}

// Fallback cadence used when trial_protocols.visit_schedule is empty.
const DEFAULT_VISITS: VisitDef[] = [
  { code: 'SCR',  label: 'Screening',     day: -14, window_before: 14, window_after: 0,  visit_type: 'screening' },
  { code: 'BL',   label: 'Baseline',      day: 0,   window_before: 0,  window_after: 3,  visit_type: 'baseline' },
  { code: 'V1',   label: 'Visit 1',       day: 30,  window_before: 3,  window_after: 3,  visit_type: 'follow_up' },
  { code: 'V2',   label: 'Visit 2',       day: 60,  window_before: 3,  window_after: 3,  visit_type: 'follow_up' },
  { code: 'V3',   label: 'Visit 3',       day: 90,  window_before: 3,  window_after: 3,  visit_type: 'follow_up' },
  { code: 'EOS',  label: 'End of Study',  day: 180, window_before: 7,  window_after: 7,  visit_type: 'eos' },
];

function normalizeVisits(raw: unknown): VisitDef[] {
  if (!raw) return DEFAULT_VISITS;
  const arr = Array.isArray(raw) ? raw : (raw as { visits?: unknown[] })?.visits;
  if (!Array.isArray(arr) || arr.length === 0) return DEFAULT_VISITS;
  return arr.map((v, i) => {
    const o = v as Record<string, unknown>;
    return {
      code: (o.code as string) || (o.visit_code as string) || `V${i}`,
      label: (o.label as string) || (o.name as string) || (o.visit_name as string),
      day: typeof o.day === 'number' ? (o.day as number) : (o.day_number as number) ?? i * 30,
      window_before: (o.window_before as number) ?? 3,
      window_after: (o.window_after as number) ?? 3,
      visit_type: (o.visit_type as string) || (i === 0 ? 'screening' : 'follow_up'),
    };
  });
}

async function expandVisits(protocolId: string, visits: VisitDef[]): Promise<number> {
  // Idempotency: skip codes already present for this protocol
  const { data: existing } = await supabase
    .from('study_visits')
    .select('visit_code')
    .eq('protocol_id', protocolId);
  const have = new Set((existing ?? []).map((r) => r.visit_code));
  const toInsert = visits
    .filter((v) => !have.has(v.code))
    .map((v, i) => ({
      protocol_id: protocolId,
      visit_name: v.label || v.code,
      visit_code: v.code,
      visit_type: v.visit_type || 'follow_up',
      day_number: v.day ?? i * 30,
      window_before: v.window_before ?? 3,
      window_after: v.window_after ?? 3,
      is_required: true,
      display_order: i,
    }));
  if (toInsert.length === 0) return 0;
  const { error } = await supabase.from('study_visits').insert(toInsert);
  if (error) throw error;
  return toInsert.length;
}

async function bundleVisitsToForms(
  trialSiteId: string,
  croTrialId: string,
  visits: VisitDef[],
  forms: ProvisionedForm[],
): Promise<number> {
  if (forms.length === 0 || visits.length === 0) return 0;

  // Existing bundle rows for idempotency check
  const { data: existing } = await supabase
    .from('visit_form_bundles')
    .select('visit_type, visit_number, ecrf_form_id')
    .eq('trial_site_id', trialSiteId)
    .eq('cro_trial_id', croTrialId);
  const key = (vt: string, vn: number, fid: string) => `${vt}|${vn}|${fid}`;
  const have = new Set((existing ?? []).map((r) => key(r.visit_type, r.visit_number, r.ecrf_form_id)));

  type BundleRow = {
    trial_site_id: string;
    cro_trial_id: string;
    visit_type: string;
    visit_number: number;
    form_type: string;
    ecrf_form_id: string;
    is_active: boolean;
  };
  const rows: BundleRow[] = [];
  visits.forEach((v, idx) => {
    forms.forEach((f) => {
      // If pack specifies applies_to_visits, honor it; else bundle to every visit.
      if (f.applies_to_visits && !f.applies_to_visits.includes(v.code)) return;
      if (have.has(key(v.code, idx, f.id))) return;
      rows.push({
        trial_site_id: trialSiteId,
        cro_trial_id: croTrialId,
        visit_type: v.code,
        visit_number: idx,
        form_type: 'ecrf',
        ecrf_form_id: f.id,
        is_active: true,
      });
    });
  });
  if (rows.length === 0) return 0;
  const { error } = await supabase.from('visit_form_bundles').insert(rows);
  if (error) throw error;
  return rows.length;
}

async function writeAudit(input: InstallTrialInput, result: InstallTrialResult) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    const payload = { input, result, ts: new Date().toISOString() };
    const text = JSON.stringify(payload);
    // SHA-256 of payload for Part 11 chain
    const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
    const hex = Array.from(new Uint8Array(hash)).map((b) => b.toString(16).padStart(2, '0')).join('');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await (supabase as any).from('compliance_audit_trail').insert({
      action: 'trial_site_install',
      entity_type: 'cro_trial_sites',
      entity_id: input.trialSiteId,
      user_id: user?.id ?? null,
      changes: payload,
      hash_after: hex,
      metadata: { trial_id: input.trialId, pack_code: input.packCode ?? null },
    });
  } catch {
    // Audit best-effort; never block install
  }
}

export function useInstallTrialSite() {
  return useMutation({
    mutationFn: async (input: InstallTrialInput): Promise<InstallTrialResult> => {
      // 1. Resolve/create the site-scoped study_protocols row + raw visit_schedule.
      // study_visits.protocol_id references study_protocols.id, not trial_protocols.id.
      const { data: protoRow, error: protoErr } = await supabase
        .from('trial_protocols')
        .select('id, visit_schedule, eligibility_requirements')
        .eq('trial_id', input.trialId)
        .maybeSingle();
      if (protoErr) throw protoErr;

      const { data: croTrial, error: croTrialErr } = await supabase
        .from('cro_trials')
        .select('id, title, phase, target_enrollment, nct_id, protocol_version, cro_organization_id')
        .eq('id', input.trialId)
        .maybeSingle();
      if (croTrialErr) throw croTrialErr;
      if (!croTrial?.id) throw new Error('Trial not found for install.');

      const protocolNumber = String(
        (protoRow?.eligibility_requirements as any)?.protocol_number
        || (croTrial as any).nct_id
        || (croTrial as any).protocol_version
        || `TRIAL-${input.trialId.slice(0, 8)}`,
      );

      const { data: existingStudyProtocol, error: studyProtoErr } = await supabase
        .from('study_protocols')
        .select('id')
        .eq('trial_site_id', input.trialSiteId)
        .eq('protocol_number', protocolNumber)
        .maybeSingle();
      if (studyProtoErr) throw studyProtoErr;

      let protocolId = existingStudyProtocol?.id ?? null;
      if (!protocolId) {
        const { data: newStudyProto, error: newStudyProtoErr } = await supabase
          .from('study_protocols')
          .insert({
            trial_site_id: input.trialSiteId,
            organization_id: (croTrial as any).cro_organization_id ?? null,
            protocol_number: protocolNumber,
            protocol_title: (croTrial as any).title || protocolNumber,
            phase: (croTrial as any).phase || 'Phase I',
            target_enrollment: (croTrial as any).target_enrollment ?? null,
            status: 'active',
          } as any)
          .select('id')
          .single();
        if (newStudyProtoErr) throw newStudyProtoErr;
        protocolId = newStudyProto.id;
      }

      const visits = normalizeVisits(protoRow?.visit_schedule);

      // 2. Expand visits
      const visitsCreated = await expandVisits(protocolId, visits);

      // 3. Provision eCRF pack (if specified)
      let forms: ProvisionedForm[] = [];
      let formsProvisioned = 0;
      let pack: InstallTrialResult['pack'] = null;
      if (input.packCode) {
        const res = await provisionECRFPack({ siteId: input.trialSiteId, packCode: input.packCode });
        forms = res.forms;
        formsProvisioned = res.insertedCount;
        pack = res.pack;
      }

      // 4. Visit -> form bundles
      const bundlesCreated = await bundleVisitsToForms(input.trialSiteId, input.trialId, visits, forms);

      // 5. Randomisation schedule advisory check
      const { data: schedRows } = await supabase
        .from('randomisation_schedules')
        .select('id')
        .eq('trial_id', input.trialId)
        .limit(1);
      const randomisationReady = (schedRows?.length ?? 0) > 0;

      const result: InstallTrialResult = {
        visitsCreated,
        formsProvisioned,
        bundlesCreated,
        randomisationReady,
        protocolId,
        pack,
      };

      await writeAudit(input, result);
      return result;
    },
  });
}
